import React, { FC, memo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faThLarge,
    faUser,
    faSun,
    faBookOpen,
    faHeart,
    faCommentDots,
    faTag,
    faFileAlt,
    faClipboardCheck,
    faBrain,
    faGift,
    faExchangeAlt,
    faHandHoldingUsd,
    faBars,
    faSignOutAlt,
    faMobileAlt,
} from '@fortawesome/free-solid-svg-icons';
import Router from '../../../navigation/router';
import { routes } from '../../../navigation';
import UserAuthService from '../../../services/user-auth';
import styles from './assets/sidebar.module.scss';
import logoIcon from '../../../assets/logo-icon.png';

import LogoutConfirmationModal from '../../common/LogoutConfirmationModal';

const Sidebar: FC = () => {
    const location = useLocation();
    const history = useHistory();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);

    const userData = UserAuthService.getData();
    const rawUsername = userData?.username;
    const username = !rawUsername || rawUsername.toLowerCase() === 'firstname' ? 'Admin User' : rawUsername;
    const email = userData?.email || 'admin@livethelife.app';

    const getInitials = (name: string) => {
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase() || 'AU';
    };
    const initials = getInitials(username);

    const handlePromptLogout = useCallback(() => {
        setShowLogoutModal(true);
    }, []);

    const handleConfirmLogout = useCallback(() => {
        setLoggingOut(true);
        UserAuthService.logout().then(() => {
            setLoggingOut(false);
            setShowLogoutModal(false);
            history.push(Router.generate(routes.LOGIN));
        });
    }, [history]);

    const isActive = (path: string) => {
        if (path === '/' || path === routes.HOME) {
            return location.pathname === '/' || location.pathname === routes.HOME;
        }
        return location.pathname.startsWith(path);
    };

    return (
        <aside className={classNames('main-sidebar', styles.sidebarWrapper)}>
            {/* Top Brand Logo */}
            <Link to={Router.generate(routes.HOME)} className={styles.brandLink}>
                <div className={styles.brandIconWrapper}>
                    <img src={logoIcon} alt="Live The Life" className={styles.brandEmblemImg} />
                </div>
                <div className={styles.brandTextArea}>
                    <span className={styles.brandTitle}>Live the Life</span>
                    <span className={styles.brandBadge}>ADMIN PANEL</span>
                </div>
            </Link>

            {/* Nav Menu */}
            <div className={styles.navContainer}>
                {/* OVERVIEW */}
                <div className={styles.navGroup}>
                    <div className={styles.groupHeading}>OVERVIEW</div>
                    <ul className={styles.navList}>
                        <li>
                            <Link
                                to={Router.generate(routes.HOME)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.HOME),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faThLarge} />
                                </span>
                                <span className={styles.navText}>Dashboard</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* USERS */}
                <div className={styles.navGroup}>
                    <div className={styles.groupHeading}>USERS</div>
                    <ul className={styles.navList}>
                        <li>
                            <Link
                                to={Router.generate(routes.USER_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.USER_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faUser} />
                                </span>
                                <span className={styles.navText}>User Management</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* CONTENT */}
                <div className={styles.navGroup}>
                    <div className={styles.groupHeading}>CONTENT</div>
                    <ul className={styles.navList}>
                        <li>
                            <Link
                                to={Router.generate(routes.DAILY_INSPIRATION_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.DAILY_INSPIRATION_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faSun} />
                                </span>
                                <span className={styles.navText}>Daily Inspirations</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.BIBLE_QUOTE_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.BIBLE_QUOTE_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faBookOpen} />
                                </span>
                                <span className={styles.navText}>God Speaks / Bible</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.DATE_NIGHT_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.DATE_NIGHT_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faHeart} />
                                </span>
                                <span className={styles.navText}>Date Night Ideas</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.CONVERSATION_STARTER_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.CONVERSATION_STARTER_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faCommentDots} />
                                </span>
                                <span className={styles.navText}>Conversation Starters</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.CONVERSATION_STARTER_CATEGORY_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.CONVERSATION_STARTER_CATEGORY_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faTag} />
                                </span>
                                <span className={styles.navText}>Conversation Starter Categories</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* ENGAGEMENT */}
                <div className={styles.navGroup}>
                    <div className={styles.groupHeading}>ENGAGEMENT</div>
                    <ul className={styles.navList}>
                        <li>
                            <Link
                                to={Router.generate(routes.ASSESSMENT_QUESTIONS_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.ASSESSMENT_QUESTIONS_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faClipboardCheck} />
                                </span>
                                <span className={styles.navText}>Assessment Questions</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.QUIZ_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.QUIZ_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faBrain} />
                                </span>
                                <span className={styles.navText}>Personality Quizzes</span>
                            </Link>
                        </li>
                    </ul>
                </div>

                {/* COMMERCE */}
                <div className={styles.navGroup}>
                    <div className={styles.groupHeading}>COMMERCE</div>
                    <ul className={styles.navList}>
                        <li>
                            <Link
                                to={Router.generate(routes.REWARD_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.REWARD_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faGift} />
                                </span>
                                <span className={styles.navText}>Rewards</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.REWARD_REDEMPTION_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.REWARD_REDEMPTION_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faExchangeAlt} />
                                </span>
                                <span className={styles.navText}>Redemptions</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.DONATION_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.DONATION_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faHandHoldingUsd} />
                                </span>
                                <span className={styles.navText}>Donation History</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.ARTICLE_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.ARTICLE_LIST),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faFileAlt} />
                                </span>
                                <span className={styles.navText}>Articles</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.USER_APP_VERSIONS)}
                                className={classNames(styles.navLink, {
                                    [styles.active]: isActive(routes.USER_APP_VERSIONS),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faMobileAlt} />
                                </span>
                                <span className={styles.navText}>User App Versions</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                to={Router.generate(routes.APP_VERSION_LIST)}
                                className={classNames(styles.navLink, {
                                    [styles.active]:
                                        isActive(routes.APP_VERSION_LIST) || isActive(routes.APP_VERSION_CREATE),
                                })}
                            >
                                <span className={styles.navIcon}>
                                    <FontAwesomeIcon icon={faMobileAlt} />
                                </span>
                                <span className={styles.navText}>App Release Version</span>
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Bottom Collapse & User Profile */}
            <div className={styles.bottomSection}>
                <button className={styles.collapseButton} data-widget="pushmenu" type="button">
                    <span className={styles.collapseIcon}>
                        <FontAwesomeIcon icon={faBars} />
                    </span>
                    <span>Collapse</span>
                </button>

                <div className={styles.userCard}>
                    <div className={styles.userInfo}>
                        <div className={styles.userAvatar}>{initials}</div>
                        <div className={styles.userDetails}>
                            <span className={styles.userName}>{username}</span>
                            <span className={styles.userEmail}>{email}</span>
                        </div>
                    </div>
                    <button className={styles.logoutBtn} onClick={handlePromptLogout} title="Log out" type="button">
                        <FontAwesomeIcon icon={faSignOutAlt} />
                    </button>
                </div>
            </div>

            <LogoutConfirmationModal
                show={showLogoutModal}
                onHide={() => setShowLogoutModal(false)}
                onConfirm={handleConfirmLogout}
                loading={loggingOut}
            />
        </aside>
    );
};

export default memo(Sidebar);
